import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import * as browser from 'lib/Browser';
import * as logging from 'util/Logging';

let enabled = browser.isBrowser;
if (enabled) {
  try {
    // In certain browsers (Safari), private browsing causes window.localStorage to have 0MB
    // of quota.
    window.localStorage.setItem('__testcanstore', '1');
    window.localStorage.removeItem('__testcanstore');
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    // Disable on error - could be from accessing window.localStorage (disabled on FF private browsing)
    // or from setItem (0MB if private Safari).
    console.warn('Disabling localStorage'); // eslint-disable-line no-console
    enabled = false;
  }
}

const kb = (length: number): string => `${((length * 2) / 1024).toFixed(2)} KB`;

// Log the 10 largest keys and the total size used to help debug our LocalStorage usage.
function logDump(keyToSet: string, valueToSet: string, error: DOMException): void {
  if (error.name === 'NS_ERROR_FILE_CORRUPTED') {
    // Browser-level error. Don't log since there's nothing we can do for corrupted browser storage. For more
    // information, see
    //   - https://developer.mozilla.org/en-US/docs/Mozilla/Errors
    //   - http://stackoverflow.com/a/18878068
    //
    // TODO(T6062): Make entry recovery possible without relying on `localStorage`.
    return;
  }
  let totalKB;
  try {
    // best-effort logging
    let totalLength = 0;
    const lsKeyKBs = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i) as string;
      const {length} = window.localStorage.getItem(key) as string;
      totalLength += length;
      lsKeyKBs.push([key, length]);
    }
    const lsKeyKB = _(lsKeyKBs)
      .chain()
      .sortBy(([key, length]) => -length)
      .slice(0, 10)
      .map(([key, length]: [key: string, length: number]) => [key, kb(length)])
      .object()
      .value();
    totalKB = kb(totalLength);
    logging.exception(error, {
      level: logging.LEVEL_WARNING,
      extra: {
        totalKB,
        lsKeyKB,
        newKB: {[keyToSet]: kb(valueToSet.length)},
      },
      fingerprint: [
        'benchling',
        'localStorage',
        'logDump',
        `key:${keyToSet}`,
        `name:${error.name ?? 'unknownName'}`,
        `code:${error.code ?? 'unknownCode'}`,
      ],
    });
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error1) {
    totalKB = -1; // error signal
    logging.exception(error, {level: logging.LEVEL_WARNING});
  }
  console.warn(`Disabling localStorage (used: ${totalKB})`); // eslint-disable-line no-console
}

function setItem(key: string, value: string, options: {reraise?: boolean} = {}): void {
  if (!enabled) {
    return undefined;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    enabled = false;
    if (options.reraise) {
      throw error;
    } else {
      // Only log the error if the parent caller doesn't handle it.
      logDump(key, value, error);
    }
    return undefined;
  }
}

function getItem(key: string): string | null | undefined {
  if (enabled) {
    return window.localStorage.getItem(key);
  } else {
    return undefined;
  }
}

function removeItem(key: string): void {
  if (enabled) {
    window.localStorage.removeItem(key);
  }
}

const localStorageWithJSON = {
  enabled() {
    return enabled;
  },
  setItem,
  getItem,
  removeItem,
  json: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setItem(key: string, value: any) {
      localStorageWithJSON.setItem(key, JSON.stringify(value));
    },
    getItem(key: string) {
      const value = localStorageWithJSON.getItem(key);
      if (value != null) {
        try {
          return JSON.parse(value);
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (e) {
          assertAndContinue(false, `Invalid JSON saved in localstorage: ${e.message}`, {jsonValue: value});
          return undefined;
        }
      } else {
        return value;
      }
    },
  },

  __enable() {
    enabled = true;
  },
  __disable() {
    enabled = false;
  },
};

export default localStorageWithJSON;
